import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import AppHeader from './AppHeader';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setStatus('error');
      setMessage('Por favor, informe seu email');
      return;
    }

    setIsLoading(true);
    setStatus('idle');
    setMessage('');

    try {
      // Configurar URL de continuação para redirecionar após o reset
      const actionCodeSettings = {
        url: `${window.location.origin}/__/auth/action`,
        handleCodeInApp: false,
      };

      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      setStatus('success');
      setMessage(`Email de recuperação enviado para ${email}. Verifique sua caixa de entrada e spam.`);
      setEmail('');
    } catch (error: any) {
      console.error('Erro ao enviar email de recuperação:', error);
      setStatus('error');
      
      // Traduzir erros comuns do Firebase
      switch (error.code) {
        case 'auth/invalid-email':
          setMessage('Email inválido');
          break;
        case 'auth/user-not-found':
          // Por segurança, não revelamos se o usuário existe
          setStatus('success');
          setMessage('Se este email estiver cadastrado, você receberá instruções para recuperar sua senha.');
          break;
        case 'auth/too-many-requests':
          setMessage('Muitas tentativas. Aguarde alguns minutos e tente novamente.');
          break;
        default:
          setMessage('Erro ao enviar email de recuperação. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#4A7C75] to-[#2D5A54] flex flex-col">
      <AppHeader />
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4A7C75] bg-opacity-10 rounded-full mb-4">
                <Mail className="w-8 h-8 text-[#4A7C75]" />
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Recuperar Senha
              </h1>
              <p className="text-gray-600">
                Informe seu email para receber instruções de recuperação
              </p>
            </div>

            {/* Mensagens de status */}
            {status === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">{message}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{message}</p>
              </div>
            )}

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-600 mb-2">
                  Email
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                    <Mail className="w-5 h-5" />
                  </span>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                    disabled={isLoading}
                    className="w-full pl-10 pr-4 py-3 bg-[#E8E8E8] rounded-full border-2 border-transparent focus:border-[#4A7C75] focus:bg-white outline-none transition text-gray-700 disabled:opacity-50"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-[#4A7C75] text-white rounded-full font-medium hover:bg-[#3A6C65] transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Enviando...' : 'Enviar Email de Recuperação'}
              </button>
            </form>

            {/* Link para voltar ao login */}
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-[#4A7C75] hover:text-[#3A6C65] font-medium transition"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para o login
              </Link>
            </div>
          </div>

          {/* Informação adicional */}
          <div className="mt-6 text-center text-white text-sm">
            <p>O link de recuperação expira em 1 hora</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
